import React, { useState } from 'react';
import { DynamicForm, FIELD_TYPES } from '../components/SelForm';

const SearchFormWithAPI = () => {
  const [submittedData, setSubmittedData] = useState(null);
  
  // API base URL - adjust if your backend runs on a different port
  const API_BASE_URL = 'http://localhost:8000';

  // Countries data for local search (keeping this local)
  const countries = [
    { id: 'us', displayName: 'United States', info: 'North America - USD' },
    { id: 'uk', displayName: 'United Kingdom', info: 'Europe - GBP' },
    { id: 'ca', displayName: 'Canada', info: 'North America - CAD' },
    { id: 'au', displayName: 'Australia', info: 'Oceania - AUD' },
    { id: 'jp', displayName: 'Japan', info: 'Asia - JPY' },
    { id: 'de', displayName: 'Germany', info: 'Europe - EUR' },
    { id: 'fr', displayName: 'France', info: 'Europe - EUR' },
    { id: 'it', displayName: 'Italy', info: 'Europe - EUR' },
    { id: 'es', displayName: 'Spain', info: 'Europe - EUR' },
    { id: 'br', displayName: 'Brazil', info: 'South America - BRL' },
    { id: 'in', displayName: 'India', info: 'Asia - INR' },
    { id: 'cn', displayName: 'China', info: 'Asia - CNY' },
  ];

  // Departments for local search
  const departments = [
    { id: 'eng', displayName: 'Engineering', info: 'Product Development & Technology' },
    { id: 'sales', displayName: 'Sales', info: 'Revenue Generation & Client Relations' },
    { id: 'mkt', displayName: 'Marketing', info: 'Brand Management & Growth' },
    { id: 'hr', displayName: 'Human Resources', info: 'People & Culture' },
    { id: 'fin', displayName: 'Finance', info: 'Financial Management & Planning' },
    { id: 'cs', displayName: 'Customer Success', info: 'Client Support & Satisfaction' },
    { id: 'pm', displayName: 'Product Management', info: 'Product Strategy & Roadmap' },
    { id: 'design', displayName: 'Design', info: 'User Experience & Visual Design' },
    { id: 'legal', displayName: 'Legal', info: 'Compliance & Contracts' },
    { id: 'ops', displayName: 'Operations', info: 'Business Operations & Efficiency' },
    { id: 'qa', displayName: 'Quality Assurance', info: 'Testing & Quality Control' },
    { id: 'data', displayName: 'Data Science', info: 'Analytics & Machine Learning' },
  ];

  // Transform function for user API responses
  const transformUserResponse = (apiResponse) => {
    if (apiResponse.users) {
      return apiResponse.users.map(user => ({
        id: user.id,
        displayName: user.name,
        info: `${user.email} - ${user.department}`
      }));
    }
    if (Array.isArray(apiResponse)) {
      return apiResponse.map(user => ({
        id: user.id,
        displayName: user.name || user.fullName,
        info: user.email || user.description
      }));
    }
    return [];
  };

  // Form fields - organized by single/multi select
  const formFields = [
    // ========== SECTION: SINGLE SELECT FIELDS ==========
    {
      id: 'sectionSingle',
      type: 'section',
      title: '🔍 Single Select Fields',
      description: 'Choose one option from each field',
      colSpan: 'full',
    },

    // Local single select
    {
      id: 'country',
      name: 'Country (Single)',
      type: FIELD_TYPES.SELECT_SEARCH,
      placeholder: 'Search and select your country...',
      hint: 'Local search - instant filtering',
      colSpan: 'half',
      validation: {
        required: 'Please select a country'
      },
      searchFieldProps: {
        options: countries,
        displayFormat: 'full',
        allowClear: true,
        maxDropdownHeight: '300px',
      }
    },

    {
      id: 'department',
      name: 'Department (Single)',
      type: FIELD_TYPES.SELECT_SEARCH,
      placeholder: 'Select your department...',
      hint: 'Local search with name-only display',
      colSpan: 'half',
      validation: {
        required: 'Please select a department'
      },
      searchFieldProps: {
        options: departments,
        displayFormat: 'nameOnly',
        allowClear: true,
      }
    },

    // API single selects
    {
      id: 'userId',
      name: 'User Search (Single)',
      type: FIELD_TYPES.SEARCH_SELECT,
      placeholder: 'Type to search users (min 2 chars)...',
      hint: 'API search - Try typing "john", "jane", or "eng"',
      colSpan: 'half',
      validation: {
        required: 'Please select a user'
      },
      searchFieldProps: {
        searchEndpoint: `${API_BASE_URL}/api/users/search`,
        searchParams: { active: true },
        minSearchLength: 2,
        debounceMs: 300,
        displayFormat: 'full',
        transformResponse: transformUserResponse,
        loadingText: 'Searching users...',
        noResultsText: 'No users found',
        searchErrorText: 'Failed to search users',
        allowClear: true,
      }
    },

    {
      id: 'managerId',
      name: 'Manager (Single)',
      type: FIELD_TYPES.SEARCH_SELECT,
      placeholder: 'Search for managers only...',
      hint: 'Filtered API search - returns only managers',
      colSpan: 'half',
      searchFieldProps: {
        searchEndpoint: `${API_BASE_URL}/api/users/search`,
        searchParams: { role: 'manager', active: true },
        minSearchLength: 2,
        debounceMs: 300,
        displayFormat: 'full',
        transformResponse: transformUserResponse,
        loadingText: 'Searching managers...',
        noResultsText: 'No managers found',
        allowClear: true,
      }
    },

    // Local multi selects
    {
      id: 'countries',
      name: 'Countries (Multiple)',
      type: FIELD_TYPES.MULTI_SELECT_SEARCH,
      placeholder: 'Select multiple countries...',
      hint: 'Local multiselect - select up to 5 countries',
      colSpan: 'half',
      validation: {
        required: 'Please select at least one country',
        validate: (value) => {
          if (Array.isArray(value) && value.length > 0) return true;
          return 'Please select at least one country';
        }
      },
      multiSelectProps: {
        options: countries,
        displayFormat: 'full',
        maxTags: 5,
        tagVariant: 'compact',
        collapseTags: true,
        collapseAfter: 3,
        selectAllOption: true,
        showSelectedCount: true,
      }
    },

    {
      id: 'departments',
      name: 'Departments (Multiple)',
      type: FIELD_TYPES.MULTI_SELECT_SEARCH,
      placeholder: 'Select multiple departments...',
      hint: 'Can work across multiple departments',
      colSpan: 'half',
      multiSelectProps: {
        options: departments,
        displayFormat: 'nameOnly',
        maxTags: null, // No limit
        tagVariant: 'default',
        collapseTags: true,
        collapseAfter: 2,
        selectAllOption: true,
      }
    },

    // API multi selects
    {
      id: 'teamMembers',
      name: 'Team Members (Multiple)',
      type: FIELD_TYPES.MULTI_SEARCH_SELECT,
      placeholder: 'Search and select team members...',
      hint: 'API multiselect - Try "john", "jane", "engineering"',
      colSpan: 'half',
      validation: {
        validate: (value) => {
          if (Array.isArray(value) && value.length >= 2) return true;
          return 'Please select at least 2 team members';
        }
      },
      multiSelectProps: {
        searchEndpoint: `${API_BASE_URL}/api/users/search`,
        searchParams: { active: true },
        minSearchLength: 2,
        debounceMs: 300,
        maxTags: 10,
        tagVariant: 'compact',
        collapseTags: true,
        collapseAfter: 4,
        displayFormat: 'full',
        transformResponse: transformUserResponse,
        loadingText: 'Searching users...',
        noResultsText: 'No users found',
        selectAllOption: true,
        hideDropdownOnSelect: false,
        showSelectedCount: true,
      }
    },

    {
      id: 'managers',
      name: 'Managers (Multiple)',
      type: FIELD_TYPES.MULTI_SEARCH_SELECT,
      placeholder: 'Select multiple managers...',
      hint: 'Can report to multiple managers',
      colSpan: 'half',
      multiSelectProps: {
        searchEndpoint: `${API_BASE_URL}/api/users/search`,
        searchParams: { role: 'manager' },
        minSearchLength: 2,
        debounceMs: 300,
        maxTags: 3,
        tagVariant: 'default',
        displayFormat: 'full',
        transformResponse: transformUserResponse,
        loadingText: 'Searching managers...',
        noResultsText: 'No managers found',
        selectAllOption: false,
        hideDropdownOnSelect: false,
      }
    },

    {
      id: 'employees',
      name: 'Employees (Multiple)',
      type: FIELD_TYPES.MULTI_SEARCH_SELECT,
      placeholder: 'Search and select employees...',
      hint: 'Different endpoint - Try "engineer" or emails',
      colSpan: 'half',
      multiSelectProps: {
        searchEndpoint: `${API_BASE_URL}/api/employees/search`,
        minSearchLength: 2,
        debounceMs: 400,
        maxTags: null, // No limit
        tagVariant: 'minimal',
        collapseTags: true,
        collapseAfter: 5,
        displayFormat: 'full',
        transformResponse: (response) => {
          return response.map(emp => ({
            id: emp.id,
            displayName: emp.fullName || emp.name,
            info: emp.description || `${emp.position} - ${emp.department}`
          }));
        },
        loadingText: 'Loading employees...',
        noResultsText: 'No employees found',
        selectAllOption: true,
      }
    },

    {
      id: 'products',
      name: 'Products (Multiple)',
      type: FIELD_TYPES.MULTI_SEARCH_SELECT,
      placeholder: 'Select multiple products...',
      hint: 'Product multiselect - Try "laptop", "chair", "PRD"',
      colSpan: 'half',
      multiSelectProps: {
        searchEndpoint: `${API_BASE_URL}/api/products/search`,
        minSearchLength: 3,
        debounceMs: 500,
        maxTags: 20,
        tagVariant: 'compact',
        collapseTags: true,
        collapseAfter: 3,
        displayFormat: 'full',
        getOptionValue: (option) => option.sku,
        getOptionLabel: (option) => option.title,
        getOptionInfo: (option) => `$${option.price} - ${option.category}`,
        loadingText: 'Loading products...',
        noResultsText: 'No products found',
        selectAllOption: true,
        showSelectedCount: true,
      }
    },

    {
      id: 'projects',
      name: 'Projects (Multiple)',
      type: FIELD_TYPES.MULTI_SEARCH_SELECT,
      placeholder: 'Assign to multiple projects...',
      hint: 'Project multiselect - Try "web", "mobile", team names',
      colSpan: 'full',
      multiSelectProps: {
        searchEndpoint: `${API_BASE_URL}/api/projects/search`,
        minSearchLength: 2,
        debounceMs: 300,
        maxTags: 5,
        tagVariant: 'default',
        collapseTags: false, // Show all tags
        displayFormat: 'full',
        getOptionValue: (option) => option.projectId,
        getOptionLabel: (option) => option.projectName,
        getOptionInfo: (option) => `${option.status} - ${option.team} Team`,
        loadingText: 'Loading projects...',
        noResultsText: 'No projects found',
        selectAllOption: true,
        hideDropdownOnSelect: false,
        showSelectedCount: true,
      }
    },

    {
      id: 'primaryTeamLead',
      name: 'Primary Team Lead (Single)',
      type: FIELD_TYPES.SEARCH_SELECT,
      placeholder: 'Select ONE team lead...',
      hint: 'Single selection only',
      colSpan: 'half',
      validation: {
        required: 'Primary team lead is required'
      },
      searchFieldProps: {
        searchEndpoint: `${API_BASE_URL}/api/users/search`,
        searchParams: { role: 'lead' },
        minSearchLength: 2,
        debounceMs: 300,
        displayFormat: 'full',
        transformResponse: transformUserResponse,
        loadingText: 'Searching team leads...',
        noResultsText: 'No team leads found',
        allowClear: true,
      }
    },

    {
      id: 'allTeamLeads',
      name: 'All Team Leads (Multiple)',
      type: FIELD_TYPES.MULTI_SEARCH_SELECT,
      placeholder: 'Select MULTIPLE team leads...',
      hint: 'Multiple selection allowed',
      colSpan: 'half',
      multiSelectProps: {
        searchEndpoint: `${API_BASE_URL}/api/users/search`,
        searchParams: { role: 'lead' },
        minSearchLength: 2,
        debounceMs: 300,
        maxTags: null,
        tagVariant: 'compact',
        collapseTags: true,
        collapseAfter: 2,
        displayFormat: 'full',
        transformResponse: transformUserResponse,
        loadingText: 'Searching team leads...',
        noResultsText: 'No team leads found',
        selectAllOption: true,
        showSelectedCount: true,
      }
    },
  ];

  // Form submission handler
  const handleSubmit = async (data) => {
    console.log('Form submitted:', data);
    setSubmittedData(data);
    alert('Form submitted successfully! Check the console for details.');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-4">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8 text-center">
          <h1 className="text-4xl font-bold text-gray-800 mb-2">
            Single & Multi Select Search Fields
          </h1>
          <p className="text-gray-600">
            Testing both single and multiselect with local and API data
          </p>
          <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-green-100 text-green-800 rounded-lg">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <span className="text-sm font-medium">
              API Status: Connected to {API_BASE_URL}
            </span>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          <DynamicForm
            fields={formFields}
            formTitle="Complete Search Field Testing"
            formDescription="Test all combinations of single/multi select with local/API data"
            onSubmit={handleSubmit}
            theme="default"
            mode="onChange"
            submitText="Submit Form"
            showReset={true}
            resetText="Clear All Fields"
            debug={false}
            layout={{
              type: 'grid',
              columns: 2,
              gap: 4
            }}
          />
        </div>

        {submittedData && (
          <div className="mt-6 bg-white rounded-2xl shadow-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">
                📊 Submitted Data
              </h3>
              <button
                onClick={() => setSubmittedData(null)}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Clear
              </button>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <pre className="text-sm text-gray-700 overflow-auto font-mono">
                {JSON.stringify(submittedData, null, 2)}
              </pre>
            </div>
            <div className="mt-4 p-4 bg-blue-50 rounded-lg">
              <h4 className="text-sm font-semibold text-blue-900 mb-2">Data Summary:</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                {Object.entries(submittedData).map(([key, value]) => (
                  <div key={key} className="flex justify-between">
                    <span className="text-blue-700">{key}:</span>
                    <span className="text-blue-900 font-medium">
                      {Array.isArray(value) ? `${value.length} selected` : value || 'Not selected'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* API Testing Instructions */}
        <div className="mt-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            🚀 Testing Guide
          </h3>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white rounded-lg p-4">
              <h4 className="font-medium text-gray-700 mb-2">Single Select Features:</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• One selection only</li>
                <li>• Clear button to remove selection</li>
                <li>• Direct value replacement</li>
                <li>• Required field validation</li>
              </ul>
            </div>
            
            <div className="bg-white rounded-lg p-4">
              <h4 className="font-medium text-gray-700 mb-2">Multi Select Features:</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Multiple selections with tags</li>
                <li>• Select/Deselect all option</li>
                <li>• Max selection limits</li>
                <li>• Collapsible tag display</li>
                <li>• Individual tag removal</li>
                <li>• Backspace to remove last</li>
              </ul>
            </div>
            
            <div className="bg-white rounded-lg p-4">
              <h4 className="font-medium text-gray-700 mb-2">Local Search:</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Instant filtering</li>
                <li>• No network requests</li>
                <li>• Countries & Departments</li>
                <li>• All data preloaded</li>
              </ul>
            </div>
            
            <div className="bg-white rounded-lg p-4">
              <h4 className="font-medium text-gray-700 mb-2">API Search:</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Debounced API calls</li>
                <li>• Loading states</li>
                <li>• Error handling</li>
                <li>• Dynamic data fetching</li>
                <li>• Custom transformers</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchFormWithAPI;